import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mic, Square, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { portalApi, transcribeAudio } from "@/lib/portalApi";
import { track } from "@/lib/analytics";
import { BEHAVIORS, BOSS_GENDERS } from "@/data/questionnaires";

const today = () => new Date().toISOString().slice(0, 10);

export default function NewIncidentPage() {
  const navigate = useNavigate();
  const [bossGender, setBossGender] = useState(null);
  const [behaviors, setBehaviors] = useState([]);
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const toggleBehavior = (id) =>
    setBehaviors((b) => (b.includes(id) ? b.filter((x) => x !== id) : [...b, id]));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const text = await transcribeAudio(blob);
          setDescription((d) => (d ? `${d} ${text}` : text));
          toast.success("Recording transcribed");
        } catch (err) {
          toast.error(err.message);
        } finally {
          setTranscribing(false);
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      toast.error("Microphone unavailable — please type instead");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (bossGender === null) return toast.error("Select your leader's gender");
    if (!behaviors.length) return toast.error("Select at least one behaviour");
    if (description.trim().length < 10) return toast.error("Please describe what happened");
    setSubmitting(true);
    try {
      const incident = await portalApi.createIncident({
        boss_gender: bossGender,
        behaviors,
        description: description.trim(),
        incident_date: new Date(date).toISOString(),
      });
      navigate(`/portal/incident/${incident.incident_id}`, { replace: true });
      track("incident_logged", { behaviours: behaviors.length });
    } catch (err) {
      if (err.status === 402) {
        toast.error(err.message);
        navigate("/portal/pricing");
      } else {
        toast.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout back="/portal">
      <form onSubmit={submit} className="max-w-3xl" data-testid="new-incident-form">
        <div className="mb-10">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Private incident log</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-3">What happened?</h1>
          <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">
            Facts, dates and quotes are what make a record useful later. Nobody but you can see this.
          </p>
        </div>

        <div className="space-y-9">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Your leader is</p>
            <div className="flex flex-wrap gap-3">
              {BOSS_GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setBossGender(g.value)}
                  data-testid={`incident-gender-${g.value}`}
                  className={`px-6 py-3 border text-sm transition-all ${
                    bossGender === g.value
                      ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                      : "border-slate-800 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Which behaviours were involved?</p>
            <div className="flex flex-wrap gap-3">
              {BEHAVIORS.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => toggleBehavior(b.id)}
                  data-testid={`incident-behavior-${b.id}`}
                  className={`px-4 py-2.5 border text-sm transition-all ${
                    behaviors.includes(b.id)
                      ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                      : "border-slate-800 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-widest text-slate-500">Describe the incident</p>
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                data-testid="incident-record-btn"
                className={`flex items-center gap-2 px-4 py-2 border text-[11px] uppercase tracking-widest transition-colors ${
                  recording
                    ? "border-red-500 text-red-400 bg-red-500/10"
                    : "border-slate-700 text-slate-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
              >
                {transcribing ? (
                  <>
                    <Loader2 size={13} className="animate-spin" /> Transcribing
                  </>
                ) : recording ? (
                  <>
                    <Square size={13} /> Stop recording
                  </>
                ) : (
                  <>
                    <Mic size={13} /> Dictate
                  </>
                )}
              </button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              data-testid="incident-description-input"
              placeholder="In the Monday review, my manager interrupted my update and said in front of eleven people that my work was 'amateur hour'. Two days later she presented my analysis to the VP as her own."
              className="w-full bg-[#0B1220] border border-slate-800 px-4 py-4 text-white text-sm leading-relaxed placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors resize-y"
            />
            {recording && (
              <p className="text-red-400 text-xs mt-2 flex items-center gap-2" data-testid="incident-recording-indicator">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /> Recording — speak naturally, then stop
              </p>
            )}
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">When did it happen?</p>
            <input
              type="date"
              value={date}
              max={today()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="incident-date-input"
              className="bg-[#0B1220] border border-slate-800 px-4 py-3 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="incident-submit-btn"
          className="mt-10 inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-60 transition-colors"
        >
          {submitting ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
          {submitting ? "Saving" : "Get my analysis"}
        </button>
      </form>
    </PortalLayout>
  );
}
